import React, { PropsWithChildren, ReactElement } from "react";
import { Area } from "@ant-design/plots";
import { Empty, Typography } from "antd";
import { PARTIAL_VISUALISATION_CONFIGURATION } from "@Components/shared/visualisationConstants";

export interface IAreaChartProps<V> {
    userId: string;
    handleActivitySelect?: (userId: string, activityInstanceId: string) => void;
    currentAnnotation?: { x: number | string; y: number };
    title: string;
    values?: V[];
    xField: keyof V;
    yField?: keyof V;
    xAxisLabelFormatter?: (value: string) => string;
    yAxisLabelFormatter?: (value: number) => string | number;
    tooltipFormatter?: (value, field) => { name: string; value: string | number };
    tooltipTitle?: (value: string) => string;
}

function AreaChart<V>(props: PropsWithChildren<IAreaChartProps<V>>): ReactElement {
    const config = {
        ...PARTIAL_VISUALISATION_CONFIGURATION,
        data: props.values,
        xField: props.xField as string,
        yField: props.yField as string,
        xAxis: {
            tickCount: 10,
            label: {
                formatter: function formatter(value) {
                    if (props.xAxisLabelFormatter) {
                        return props.xAxisLabelFormatter(value);
                    } else {
                        return value;
                    }
                },
            },
        },
        yAxis: {
            label: {
                formatter: function formatter(value) {
                    if (props.yAxisLabelFormatter) {
                        return props.yAxisLabelFormatter(value);
                    } else {
                        return value;
                    }
                },
            },
        },
        tooltip: {
            formatter: (value) => {
                if (props.tooltipFormatter) {
                    return props.tooltipFormatter(value, props.yField);
                } else {
                    return { name: props.yField as string, value: value[props.yField as string] };
                }
            },
            title: (title: string) => {
                if (props.tooltipTitle) {
                    return props.tooltipTitle(title);
                }
                return title;
            },
        },
        annotations: props.currentAnnotation
            ? ([
                  {
                      type: "html",
                      html: `<div style="position: relative; left: -32px; top: -32px; pointer-events: none">
              <p class="newUI-glass" style="font-weight: 600; text-align: center; position: absolute; left: -10px; top: -60px; width: 90px">This Activity</p>
              <svg width="64px" height="64px" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
                <style type="text/css">
                  .annotation0{fill:#D01617;}
                  .annotation1{fill:rgba(255,0,0,0.2);}
                </style>
                <path class="annotation0" d="M31.7,20.1c-6.6,0-12,5.4-12,12s5.4,12,12,12s12-5.4,12-12S38.3,20.1,31.7,20.1z M31.7,22.1c5.5,0,10,4.5,10,10
                    s-4.5,10-10,10s-10-4.5-10-10S26.2,22.1,31.7,22.1z M31.7,29.1c-1.7,0-3,1.3-3,3c0,1.7,1.3,3,3,3c1.7,0,3-1.3,3-3
                    C34.7,30.5,33.3,29.1,31.7,29.1z"/>
                <path class="annotation0" d="M31.7,51.6c-10.7,0-19.5-8.7-19.5-19.5c0-10.7,8.7-19.5,19.5-19.5c10.7,0,19.5,8.7,19.5,19.5
                    C51.2,42.9,42.4,51.6,31.7,51.6z M31.7,13.7c-10.2,0-18.5,8.3-18.5,18.5s8.3,18.5,18.5,18.5c10.2,0,18.5-8.3,18.5-18.5
                    S41.9,13.7,31.7,13.7z"/>
                <path class="annotation1" d="M31.7,13.7c-10.2,0-18.5,8.3-18.5,18.5s8.3,18.5,18.5,18.5c10.2,0,18.5-8.3,18.5-18.5S41.9,13.7,31.7,13.7z"/>
              </svg>
            </div>`,
                      position: () => {
                          return [props.currentAnnotation.x, props.currentAnnotation.y];
                      },
                  },
              ] as any)
            : [],
        areaStyle: () => {
            return {
                fill: "l(270) 0:#eeeeff 0.5:#5c90c1 1:#1890ff",
            };
        },
    };

    if (props.values.length < 2) {
        return <Empty />;
    }

    return (
        <div>
            <Typography.Title level={3}>{props.title}</Typography.Title>
            <Area
                {...config}
                onReady={(plot) => {
                    plot.on("plot:click", (evt) => {
                        const { x, y } = evt;
                        const tooltipData = plot.chart.getTooltipItems({ x, y });
                        if (
                            tooltipData[0]?.data?.ActivityInstanceId !== undefined &&
                            props.handleActivitySelect !== null
                        ) {
                            props.handleActivitySelect(props.userId, tooltipData[0]?.data?.ActivityInstanceId);
                        }
                    });
                }}
            />
        </div>
    );
}

export default AreaChart;
